import {VetClinic} from "./vet-clinic.model";
import {AdditionalUserVetcode} from "./additional-user-vetcode.model";

/**
 * Model Użytkownika Dashboardu.
 *
 * Reprezentuje osobę mającą dostęp do panelu administracyjnego. Może to być:
 * - Administrator Globalny (GeneralAdmin).
 * - Administrator Kliniki/Sieci Klinik (ClientAdmin).
 * - Personel Kliniki (różne role dla różnych działań).
 *
 * Kluczowe cechy dla Dashboardu:
 * - Posiada role i uprawnienia (`roles`).
 * - Może być przypisany do wielu placówek jednocześnie (`additionalUserVetcodes`).
 * - Posiada identyfikatory biznesowe (`vClientId`).
 */
export class User {

  public firstName: string;
  public lastName: string;
  public mobilePhone: string;
  public email: string;

  /**
   * ID użytkownika w bazie danych aplikacji mobilnej ("VetineoUser").
   * Jeśli to pole jest wypełnione, oznacza to, że ten pracownik/admin posiada również
   * konto w aplikacji mobilnej.
   */
  public appUserId: number;

  /**
   * Globalny, unikalny identyfikator użytkownika (SSO / Auth Service).
   */
  public uuid: string;

  /**
   * Ciąg znaków określający uprawnienia.
   * Role rozdzielone są średnikami.
   */
  public roles: string;

  /**
   * Flaga określająca, czy użytkownik jest Partnerem.
   */
  public isPartnerType: boolean;

  /**
   * Flaga usunięcia.
   */
  public accountDeleted: boolean;

  /**
   * ID Klienta Biznesowego).
   * GeneralAdmin nie będzie miał przypisanego vClientId, gdyż jest rolą nadrzędną.
   */
  public vClientId: string;

  /**
   * ID konkretnej Placówki, która jest domyślnym kontekstem pracy użytkownika.
   * ClientAdmin nie będzie miał przypisanego vetcodeId, gdyż jest rolą nadrzędną.
   */
  public vetcodeId: string;

  public vClientUUID: string;

  /**
   * Zewnętrzny identyfikator z KlinikiXP.
   */
  public xpCustId: string;

  public id: number;

  public created: string;

  /**
   * Obiekt z pełnymi danymi domyślnej kliniki.
   */
  public vetClinic: VetClinic;

  /**
   * Czy użytkownik ma włączony moduł wiadomości.
   */
  public hasMessagesEnabled: boolean;

  /**
   * Numer telefonu zweryfikowany.
   */
  public authenticatedMobilePhone: string;

  /**
   * Lista dodatkowych placówek, do których użytkownik ma dostęp.
   */
  public additionalUserVetcodes: AdditionalUserVetcode[];

  /**
   * Jeśli użytkownik jest Lekarzem Weterynarii, to pole zawiera UUID jego profilu lekarskiego.
   */
  public vetDoctorUUID: string;

  constructor() {
  }

  /**
   * Sprawdza, czy użytkownik posiada daną rolę.
   *
   * @param role Szukana rola.
   * @returns `true` jeśli użytkownik ma rolę LUB jest 'admin'.
   */
  public hasRole(role: string) {
    return role && this.roles && (this.roles.indexOf(role) >= 0 || this.roles.indexOf('admin') >= 0);
  }

  /**
   * Sprawdza, czy użytkownik NIE JEST powiązany z kontem aplikacji mobilnej ("VetineoUser").
   * Zazwyczaj `true` dla personelu administracyjnego, który nie korzysta z apki dla pacjentów.
   */
  public isNotVetineoUser() {
    return !this.appUserId;
  }

  /**
   * Sprawdza, czy użytkownik jest powiązany z kontem klienckim (aplikacją mobilną).
   */
  public isClient() {
    return this.appUserId !== undefined && this.appUserId !== null;
  }

  public isNotClient() {
    return this.isClient() === false;
  }

}
